import { Delimiter, UnitPosition, NumberFormatConfig, NumberFormatType } from 'common/building-blocks/text/constants';

export default function useNumberFormatter(text: string, config?: NumberFormatConfig | null) {
	if (!config?.type) {
		return text;
	}

	return text.replace(/\d+(\.\d+)?/g, match => {
		const num = parseFloat(match);
		if (Number.isNaN(num)) return match;

		switch (config?.type) {
			case NumberFormatType.Compact: {
				return applyCompact(num, 0);
			}

			case NumberFormatType.Percent: {
				return applyPercent(num);
			}

			case NumberFormatType.CommaSeparated: {
				return applyDelimiter(num, 'comma');
			}

			case NumberFormatType.Custom: {
				let result = num.toString();
				const precision = Number.isNaN(+config.precision) ? 0 : +config.precision;

				// Compact mode already handles precision within itself:
				if (config.compact) {
					result = applyCompact(num, precision);
				} else {
					result = applyPrecision(num, precision);
				}

				if (config.delimiter) {
					// Capture the whole part and optionally the decimal part
					// (\d+) captures the digits before decimal
					// (\.\d+)? optionally captures the decimal point and digits after it
					result = result.replace(/(\d+)(\.\d+)?/g, (fullMatch, wholeNumber, decimalPart) => {
						const formattedWholeNumber = applyDelimiter(+wholeNumber, config.delimiter);
						return formattedWholeNumber + (decimalPart || '');
					});
				}

				result = applyUnit(result, config.unit, config.unitPosition);

				return result;
			}

			default: {
				return match;
			}
		}
	});
}

function applyPrecision(num: number, precision: number) {
	return precision > 0 ? num.toFixed(precision) : Math.round(num).toString();
}

function applyCompact(num: number, precision: number): string {
	const absNum = Math.abs(num);

	if (absNum < 1000) {
		return applyPrecision(num, precision);
	}

	// Regexp to remove trailing zeros in decimal numbers
	const rx = new RegExp(`\\.0{${precision}}0+$|(\\.[0-9]*[1-9])0{${precision}}0+$/`);
	const si = [
		{ value: 1, symbol: '' },
		{ value: 1e3, symbol: 'K' },
		{ value: 1e6, symbol: 'M' },
		{ value: 1e9, symbol: 'B' },
		{ value: 1e12, symbol: 'T' },
	];

	let i: number;
	for (i = si.length - 1; i > 0; i -= 1) {
		if (absNum >= si[i].value) {
			break;
		}
	}

	const compactPrecision = precision > 0 ? precision : absNum >= 1e6 ? 2 : 1;
	const val = (num / si[i].value).toFixed(compactPrecision).replace(rx, '$1');
	return val + si[i].symbol;
}

function applyPercent(num: number) {
	return `${num * 100}%`;
}

function applyUnit(numStr: string, unit: string, unitPosition: UnitPosition): string {
	if (!unit) {
		return numStr;
	}

	return unitPosition === 'before' ? unit + numStr : numStr + unit;
}

function applyDelimiter(num: number, delimiter: Delimiter) {
	const delimiterMap: Record<Delimiter, string> = {
		comma: ',',
		dot: '.',
		space: ' ',
	};
	const delimiterChar = delimiter ? delimiterMap[delimiter] : ',';
	const parts = num.toString().split('.');

	if (parts.length > 1) {
		// Handle decimal numbers
		return `${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, delimiterChar)}.${parts[1]}`;
	}
	// Handle whole numbers
	return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, delimiterChar);
}
